import { Suspense, lazy } from 'react';
import type { NomeDeIcone } from '@vestigo/core/area-icones-da-entrada';
import { atributosDoLogo, imagemDaMarca } from '@vestigo/core/area-marca';
import type { TelaDeEntrada } from '../lib/api.js';
import type { PecasDaEntrada } from './Moldura.js';
import { telaComDicionario } from '../lib/tela-com-dicionario.js';

/**
 * ── AS DUAS ESTRUTURAS DE SEMPRE, E AS PEÇAS QUE TODAS PARTILHAM (L1) ───────
 *
 * Sóbria e Lateral são o que a tela de entrar era — classe por classe — e por
 * isso moram no pedaço de entrada. As peças (`Marca`, `Icone`, `Voz`, `Foto`)
 * são as mesmas que as quatro estruturas sob demanda usam; ficam aqui para o
 * pedaço lazy não as duplicar.
 */
export interface PropsDaEstrutura {
  cara: TelaDeEntrada | null;
  pecas: PecasDaEntrada;
  inerte: boolean;
}

/* O catálogo de ícones só se paga quando a área escolheu um: pedaço à parte. */
const IconeDoCatalogo = telaComDicionario(() => import('./Icone.js'));

/** A altura em que a tela de entrar desenha o logo — `h-14` e `h-8` em px. */
const ALTURA_DO_LOGO = { grande: 56, normal: 32 } as const;

/** O logo OU o nome — nunca os dois: um logo com o nome ao lado é a palavra repetida. */
export function Marca({ cara, grande = false }: { cara: TelaDeEntrada | null; grande?: boolean }) {
  /* O MESMO desembrulhador do cabeçalho do aluno — ver `core/area/marca-da-area.ts`.
     Aqui o servidor manda o endereço e não o objeto, e é justamente por isso que
     esta linha existe: a tela não tem de saber qual das duas formas chegou. */
  const logo = imagemDaMarca(cara?.logo);
  if (logo) {
    return (
      <img
        {...atributosDoLogo(logo, grande ? ALTURA_DO_LOGO.grande : ALTURA_DO_LOGO.normal)}
        alt={cara?.nome ?? ''}
        className={grande ? 'mb-6 h-14 w-auto object-contain' : 'mb-8 h-8 w-auto object-contain'}
        data-vst-regiao="entrada-marca"
      />
    );
  }
  if (!cara?.nome) return null;
  return (
    <p
      className={grande ? 'mb-6 text-2xl font-semibold' : 'mb-8 text-sm'}
      style={grande ? { fontFamily: 'var(--fonte-da-voz)' } : { color: 'var(--texto-suave)' }}
      data-vst-regiao="entrada-marca"
    >
      {cara.nome}
    </p>
  );
}

export function Icone({
  nome,
  tamanho = 32,
}: {
  nome: NomeDeIcone | null | undefined;
  tamanho?: number;
}) {
  if (!nome) return null;
  return (
    <Suspense fallback={null}>
      <IconeDoCatalogo nome={nome} tamanho={tamanho} />
    </Suspense>
  );
}

/** A foto com degraus: `<img>` absoluto e coberto, nunca `background-image`. */
export function Foto({
  cara,
  sizes,
  prioridade = 'alta',
  className = '',
}: {
  cara: TelaDeEntrada | null;
  sizes: string;
  prioridade?: 'alta' | 'baixa';
  className?: string;
}) {
  if (!cara?.entrada.imagem) return null;
  return (
    <img
      src={cara.entrada.imagem}
      srcSet={cara.entrada.imagemSrcset || undefined}
      sizes={sizes}
      width={cara.entrada.imagemLargura ?? undefined}
      height={cara.entrada.imagemAltura ?? undefined}
      alt=""
      aria-hidden
      decoding="async"
      loading={prioridade === 'alta' ? 'eager' : 'lazy'}
      fetchPriority={prioridade === 'alta' ? 'high' : 'low'}
      className={`absolute inset-0 h-full w-full object-cover object-center ${className}`}
      data-vst-regiao="entrada-foto"
    />
  );
}

/** A chamada abaixo do formulário — a posição de Sóbria e Lateral, como sempre foi. */
export function ChamadaAbaixo({ cara }: { cara: TelaDeEntrada | null }) {
  if (!cara?.entrada.chamada) return null;
  return (
    <p
      className="mt-8 text-sm"
      style={{ color: 'var(--texto-suave)' }}
      data-vst-regiao="entrada-chamada"
    >
      {cara.entrada.chamada}
    </p>
  );
}

/** O corpo funcional da tela: o h1, a intro, o formulário, o secundário, o rodapé. */
export function Corpo({ pecas, inerte }: { pecas: PecasDaEntrada; inerte: boolean }) {
  return (
    /* Inerte (a prévia da bancada): os campos e os links morrem, mas a REGIÃO
       continua a receber o clique — é ele que leva a ficha ao grupo certo. Por
       isso o `pointer-events: none` fica num envoltório interno, e não aqui. */
    <div data-vst-regiao="entrada-formulario">
      <div style={inerte ? { pointerEvents: 'none' } : undefined} aria-hidden={inerte || undefined}>
        <h1
          className="text-2xl font-semibold"
          style={{ fontSize: 'calc(1.5rem * var(--escala, 1))' }}
        >
          {pecas.titulo}
        </h1>
        {pecas.intro}
        {pecas.formulario}
        {pecas.secundario}
        {pecas.rodape}
      </div>
    </div>
  );
}

/* ── Sóbria: a de sempre, classe por classe ─────────────────────────────── */
export function Sobria({ cara, pecas, inerte }: PropsDaEstrutura) {
  return (
    <div className="min-h-screen">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
        <div>
          <Icone nome={cara?.entrada.icone} />
          <Marca cara={cara} />
          <Corpo pecas={pecas} inerte={inerte} />
          <ChamadaAbaixo cara={cara} />
        </div>
      </div>
    </div>
  );
}

/* ── Lateral: a foto na metade esquerda (some no celular), a coluna à direita ─ */
export function Lateral({ cara, pecas, inerte }: PropsDaEstrutura) {
  return (
    <div className="flex min-h-screen">
      <div className="relative hidden w-1/2 md:block" aria-hidden>
        <Foto cara={cara} sizes="50vw" prioridade="baixa" />
      </div>
      <div className="flex min-h-screen flex-1 flex-col justify-center px-6">
        <div className="mx-auto w-full max-w-md">
          <Icone nome={cara?.entrada.icone} />
          <Marca cara={cara} />
          <Corpo pecas={pecas} inerte={inerte} />
          <ChamadaAbaixo cara={cara} />
        </div>
      </div>
    </div>
  );
}
